/**
 * Helper to detect if a string contains LaTeX commands
 * Aligned with mobi-admin's containsLaTeX logic
 */
export function containsLaTeX(text: string): boolean {
    if (!text) return false;
    // Check for common LaTeX markers
    return text.includes('\\') || 
           text.includes('$') || 
           text.includes('\\int') || 
           text.includes('\\mathrm') || 
           text.includes('\\begin{') ||
           text.includes('\\end{') ||
           text.includes('&amp;') ||  
           text.includes('\\text{');
}

/**
 * Format LaTeX string for rendering with KaTeX/react-latex
 * Aligned with mobi-admin's formatLatex logic
 */
export function formatLatexText(unformattedLatex: string): string {
    if (!unformattedLatex) return "";

    // Handle specific pattern: \(\displaylines{\text{...}\\ \begin{aligned}...\end{aligned}}\)
    if (unformattedLatex.includes('\\displaylines{') && unformattedLatex.includes('\\begin{aligned}')) {
        // Multiple patterns to catch various forms of this malformed structure
        const patterns = [
            // Main pattern - handles the specific case we're seeing
            /\\?\(\\displaylines\{\\text\{([^}]*)\}\s*\\\\\s*\\begin\{aligned\}([^}]*?)\s*&?\s*&?\s*\\end\{aligned\}\s*\}\s*\\?\)/,
            // Pattern without spaces
            /\\?\(\\displaylines\{\\text\{([^}]*)\}\\\\\\begin\{aligned\}([^}]*?)&?&?\\end\{aligned\}\}\\?\)/,
            // More flexible pattern as fallback
            /\\?\(\\displaylines\{\\text\{([^}]*)\}.*?\\begin\{aligned\}([^}]*?).*?\\end\{aligned\}.*?\}.*?\\?\)/
        ];
        
        for (let pattern of patterns) {
            const match = unformattedLatex.match(pattern);
            if (match) {
                const textPart = match[1];
                const mathPart = match[2].trim().replace(/&/g, '').trim(); // Remove alignment markers
                return `$$\\begin{aligned}\\text{${textPart}} \\\\ ${mathPart}\\end{aligned}$$`;
            }
        }
    }

    let formatted = unformattedLatex
        // Convert align to aligned for better compatibility
        .replace(/\\begin{align}/g, '\\begin{aligned}')
        .replace(/\\end{align}/g, '\\end{aligned}')
        // Handle \begin{align*} environments
        .replace(/\\begin{align\*}/g, "\\begin{aligned}")
        .replace(/\\end{align\*}/g, "\\end{aligned}")
        // Handle displaylines - convert to aligned for better compatibility
        .replace(/\\displaylines\{/g, '\\begin{aligned}')
        .replace(/\}(?=\s*\\?\)?\s*$)/g, '\\end{aligned}')
        // Handle HTML entities
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\\bm/g, '')
        // Handle line breaks
        .replace(/\n/g, ' \\\\ ')
        // Clean up multiple consecutive spaces
        .replace(/\s+/g, ' ')
        // Remove leading/trailing whitespace
        .trim();

    // Clean up any remaining malformed inline delimiters around aligned environments
    if (formatted.includes('\\begin{aligned}')) {
        // Remove \( and \) around aligned environments (inline delimiters don't work with aligned)
        formatted = formatted.replace(/^\\?\(\\begin\{aligned\}/, '\\begin{aligned}');
        formatted = formatted.replace(/\\end\{aligned\}\s*\}?\s*\\?\)$/, '\\end{aligned}');
        
        // Also handle cases where there are extra braces
        formatted = formatted.replace(/\\end\{aligned\}\s*\}+\s*$/, '\\end{aligned}');
    }

    // Case 1: Pure numeric values inside \[...\] → Return plain number
    const numericMatch = formatted.match(/^\s*\\\[\s*(\d+)\s*\\\]\s*$/);
    if (numericMatch) {
        return numericMatch[1];
    }

    // Case 2: Detect if it's \text{...} inside \[...\] → Return plain text
    const textMatch = formatted.match(/^\s*\\\[\s*\\text\{([^}]*)\}\s*\\\]\s*$/);
    if (textMatch) {
        return textMatch[1].trim();
    }

    // Case 3: If it contains aligned environment, wrap with display math
    if (formatted.includes('\\begin{aligned}')) {
        // Remove any existing \[ \] wrappers that conflict with aligned
        if (formatted.startsWith('\\[') && formatted.endsWith('\\]')) {
            formatted = formatted.substring(2, formatted.length - 2);
        }
        // Remove any existing $$ wrappers to avoid double-wrapping
        if (formatted.startsWith('$$') && formatted.endsWith('$$')) {
            formatted = formatted.substring(2, formatted.length - 2);
        }
        return `$$${formatted}$$`;
    }

    // Case 4: Numeric values with superscripts/exponents → Keep as math
    if (/^\s*\\\[(.*\^.*)\s*\\\]\s*$/.test(formatted)) {
        formatted = formatted.replace(/^\s*\\\[\s*|\s*\\\]\s*$/g, "");
        return `$${formatted}$`;
    }

    // Case 5: Convert block-level LaTeX (\[...\]) to $$...$$
    if (/^\s*\\\[/.test(formatted) && /\\\]\s*$/.test(formatted)) {
        formatted = formatted.replace(/^\s*\\\[\s*|\s*\\\]\s*$/g, "");
        return `$$${formatted}$$`;
    }

    // Case 6: Convert inline LaTeX ($...$) and preserve formatting
    if (/^\s*\$/.test(formatted) && /\$\s*$/.test(formatted)) {
        return formatted; // Already properly formatted
    }

    // Case 7: If it contains only \text{...}, wrap it in inline $
    if (/^\\text\{.*\}$/.test(formatted)) {
        return `$${formatted}$`;
    }

    // Case 8: If it has LaTeX commands but no delimiters, add appropriate ones
    if (formatted.includes('\\') || formatted.includes('^') || formatted.includes('_')) {
        // Check if it looks like display math (multi-line or complex)
        if (formatted.includes('\\\\') || formatted.length > 50) {
            return `$$${formatted}$$`;
        } else {
            return `$${formatted}$`;
        }
    }

    // Default case: return as-is
    return formatted;
}